import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { Box, Button, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { paths } from '@common/types';
import { useGeneral } from './hooks';
import { USStateSelect } from 'components/USStateSelect';
import { FormGooglePlacesAutocomplete, SaveChangesModal } from '@components';
import { Select } from 'ui';

interface GeneralProps {
  building: paths[`/building/{record_id}`]['get']['responses']['200']['content']['application/json'];
}

export const General = ({ building }: GeneralProps) => {
  const {
    formMethods,
    onSubmit,
    isSubmitEnabled,
    isSubmitting,
    organizationOptions,
    operatorOptions,
    partnerOptions,
    setValue,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useGeneral(building);

  const { control } = formMethods;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
            gap: '1rem',
          }}
        >
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.buildings.addModal.name')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />

          <Controller
            name='organization_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                label={t('adminPanel.buildings.addModal.organization')}
                error={!isNil(error)}
                required
                disabled
                onChange={event => {
                  field.onChange(event);
                  setValue('partner_id', '');
                  setValue('operator_id', '');
                }}
                options={organizationOptions}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='partner_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                label={t('adminPanel.buildings.addModal.partner')}
                error={!isNil(error)}
                required
                disabled
                onChange={event => {
                  field.onChange(event);
                  setValue('operator_id', '');
                }}
                options={partnerOptions}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='operator_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                label={t('adminPanel.buildings.addModal.operator')}
                variant='outlined'
                error={!isNil(error)}
                required
                options={operatorOptions}
                helperText={error?.message}
              />
            )}
          />

          <Typography
            variant='subtitle2'
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            {t('adminPanel.buildings.addModal.contactInformation')}
          </Typography>

          <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='street'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.addressLine1')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='suite'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.addressLine2')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='city'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.city')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='state'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <USStateSelect
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ''}
                  required
                />
              )}
            />
          </Box>

          <Controller
            name='zip_code'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.buildings.addModal.zip')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />

          <Controller
            name='county'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.buildings.addModal.county')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='phone_number'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.phoneNumber')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
            <Controller
              name='fax_number'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.faxNumber')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              pt: '0.5rem',
              position: 'sticky',
              bottom: '0',
              left: 0,
              bgcolor: theme => theme.palette.common.white,
              zIndex: 10,
            }}
          >
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={!isSubmitEnabled}
              loading={isSubmitting}
              loadingPosition='center'
            >
              {t('general.saveChanges')}
            </Button>
          </Box>
        </Form>
      </FormProvider>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Box>
  );
};
